import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/SEOHead';
import { services, serviceAreas, reviews, certifications } from '../../mocks/services';
import { useState, useEffect } from 'react';

const HomePage = () => {
  const [currentReview, setCurrentReview] = useState(0);
  const [glowIndex, setGlowIndex] = useState(-1);
  const brandText = "ELECTRICIAN SOLUTIONS FRESNO";
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    urgency: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ElectricalContractor',
    name: 'Fresno Electrical Services',
    description: 'Licensed electrician in Fresno, CA providing residential, commercial, and emergency electrical services',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '123 Main Street',
      addressLocality: 'Fresno',
      addressRegion: 'CA',
      postalCode: '93721',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '36.7378',
      longitude: '-119.7871'
    },
    url: siteUrl,
    telephone: '+1-559-123-4567',
    priceRange: '$$',
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California'
    }
  };

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setGlowIndex(i);
      i++;
      if (i >= brandText.length) {
        i = 0;
        setTimeout(() => setGlowIndex(-1), 300);
      }
    }, 80);
    return () => clearInterval(interval);
  }, []);

  const nextReview = () => {
    setCurrentReview((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentReview((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const SLACK_WEBHOOK_URL = 'YOUR_SLACK_WEBHOOK_URL';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const slackPayload = {
        text: `📋 *New Quote Request — Homepage*\n\n*Name:* ${formData.name}\n*Email:* ${formData.email}\n*Phone:* ${formData.phone}\n*Urgency:* ${formData.urgency}\n*Message:* ${formData.message}`,
      };

      const [readyResponse] = await Promise.allSettled([
        fetch('https://readdy.ai/api/form/d6ontcl39lnhn4hh1e30', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        }),
        fetch(SLACK_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(slackPayload),
        }),
      ]);

      if (readyResponse.status === 'fulfilled' && readyResponse.value.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', urgency: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <>
      <SEOHead
        title="Fresno Electrical Services | Licensed Electrician Fresno CA"
        description="Professional licensed electrician in Fresno, CA. 24/7 emergency electrical services, residential & commercial electrical work. Fast response, quality guaranteed."
        canonical="/"
        keywords="electrician Fresno, electrical services Fresno CA, licensed electrician, emergency electrician"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <style>{`
          @keyframes electricFlicker {
            0%, 100% { opacity: 1; text-shadow: 0 0 8px #FFD700, 0 0 20px #FFD700, 0 0 40px #FFA500; }
            25% { opacity: 0.85; text-shadow: 0 0 4px #FFD700, 0 0 10px #FFD700; }
            50% { opacity: 1; text-shadow: 0 0 12px #FFD700, 0 0 30px #FFD700, 0 0 60px #FFA500, 0 0 80px #FF8C00; }
            75% { opacity: 0.9; text-shadow: 0 0 6px #FFD700, 0 0 15px #FFD700; }
          }
          @keyframes boltPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.2); }
          }
          @keyframes callPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.5); }
            50% { box-shadow: 0 0 0 10px rgba(255, 215, 0, 0); }
          }
          @keyframes phoneBounce {
            0%, 100% { transform: rotate(0deg); }
            20% { transform: rotate(-15deg); }
            40% { transform: rotate(15deg); }
            60% { transform: rotate(-10deg); }
            80% { transform: rotate(10deg); }
          }
          .electric-char {
            display: inline-block;
            transition: all 0.1s ease;
            color: #FFD700;
          }
          .electric-char.lit {
            animation: electricFlicker 0.3s ease-in-out;
            color: #fff;
            text-shadow: 0 0 8px #FFD700, 0 0 20px #FFD700, 0 0 40px #FFA500;
          }
          .bolt-icon {
            animation: boltPulse 1.2s ease-in-out infinite;
          }
          .call-banner {
            animation: callPulse 2s ease-in-out infinite;
          }
          .phone-icon {
            animation: phoneBounce 2s ease-in-out infinite;
          }
        `}</style>

        <Navbar />

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center pt-24 md:pt-20">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/3086ff0db2cc27fb6902144ee3519fae.jpg" 
              alt="Professional Electrician Fresno" 
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/45"></div>
          </div>

          <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 w-full py-16 md:py-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                {/* Animated Electric Brand Text */}
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-5 flex-nowrap overflow-hidden">
                  <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center bolt-icon">
                    <i className="ri-flashlight-fill text-[#FFD700] text-sm"></i>
                  </span>
                  <span className="font-black tracking-[0.1em] sm:tracking-[0.2em] text-[9px] sm:text-sm select-none whitespace-nowrap">
                    {brandText.split('').map((char, i) => (
                      <span
                        key={i}
                        className={`electric-char${glowIndex === i ? ' lit' : ''}`}
                      >
                        {char === ' ' ? '\u00A0' : char}
                      </span>
                    ))}
                  </span>
                  <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center bolt-icon" style={{ animationDelay: '0.6s' }}>
                    <i className="ri-flashlight-fill text-[#FFD700] text-sm"></i>
                  </span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-4 sm:mb-6 leading-tight">
                  FRESNO'S<br />LICENSED<br />ELECTRICIANS
                </h1>
                <p className="text-base sm:text-xl text-gray-200 mb-6 sm:mb-8 font-light">
                  Fresno CA's Trusted C-10 Certified Electrical Contractor Since 2010
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
                  <a href="#get-quote" className="bg-[#FFD700] text-[#1a1a1a] px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wide hover:bg-[#FFC700] transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                    Get Free Quote
                    <i className="ri-arrow-right-line"></i>
                  </a>
                  <a href="#get-quote" className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wide hover:bg-white hover:text-[#1a1a1a] transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                    <i className="ri-alarm-warning-line"></i>
                    24/7 Emergency
                  </a>
                </div>

                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-white text-xs sm:text-sm uppercase tracking-wider">
                  <span className="font-bold">Licensed</span>
                  <span className="text-[#FFD700]">•</span>
                  <span className="font-bold">Insured</span>
                  <span className="text-[#FFD700]">•</span>
                  <span className="font-bold">5-Star Rated</span>
                </div>
              </div>

              <div className="relative hidden lg:block">
                <div className="absolute bottom-8 right-8 bg-white rounded-full p-6 shadow-2xl">
                  <div className="text-center">
                    <div className="text-[#FFD700] text-4xl font-black">C-10</div>
                    <div className="text-[#1a1a1a] text-xs font-bold uppercase">Licensed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Fresno SEO Intro Section */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-5">
                  Fresno, CA's #1 Electrician
                </span>
                <h2 className="text-3xl sm:text-4xl font-black text-[#1a1a1a] mb-5 leading-tight">
                  Your Local Fresno Electrician — <span className="text-[#FFD700]">Licensed, Trusted &amp; Ready</span>
                </h2>
                <p className="text-gray-600 text-base mb-4 leading-relaxed">
                  Electrician Solutions Fresno is the Central Valley's premier C-10 licensed electrical contractor. From North Fresno to Downtown, Clovis to Madera, our certified Fresno electricians deliver safe, code-compliant electrical work for homes and businesses of every size.
                </p>
                <p className="text-gray-600 text-base mb-6 leading-relaxed">
                  Whether you need a panel upgrade, EV charger installation, smart home wiring, or a 24/7 emergency electrician in Fresno, our NECA-certified team is on call and ready to help. We've served <strong>500+ Fresno homeowners and businesses</strong> with a 4.9-star rating and a 100% satisfaction guarantee.
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { icon: 'ri-shield-check-fill', label: 'C-10 Licensed' },
                    { icon: 'ri-award-fill', label: 'NECA Certified' },
                    { icon: 'ri-time-fill', label: '24/7 Emergency' },
                    { icon: 'ri-map-pin-fill', label: 'Fresno, CA Based' },
                  ].map((badge) => (
                    <div key={badge.label} className="flex items-center gap-2 bg-[#f5f5f5] px-4 py-2 rounded-full">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${badge.icon} text-[#FFD700] text-base`}></i>
                      </span>
                      <span className="text-sm font-bold text-[#1a1a1a]">{badge.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl h-72 sm:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20electrician%20team%20in%20uniform%20standing%20confidently%20in%20front%20of%20a%20modern%20Fresno%20California%20residential%20neighborhood%20with%20clear%20blue%20sky%20and%20palm%20trees%20in%20background%20showing%20pride%20and%20local%20expertise%20in%20electrical%20contracting&width=800&height=600&seq=fresno_team_001&orientation=landscape"
                  alt="Licensed electricians serving Fresno CA"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white font-bold text-lg">Proudly Serving Fresno Since 2010</p>
                  <p className="text-[#FFD700] text-sm font-medium">Central Valley's Most Trusted Electrician</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-12 sm:py-20 bg-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 sm:mb-12 gap-4">
              <h2 className="text-3xl sm:text-5xl font-black text-[#1a1a1a]">OUR SERVICES</h2>
              <Link to="/services/residential-electrical" className="text-[#FFD700] font-bold hover:underline flex items-center gap-2 cursor-pointer whitespace-nowrap">
                View All Services
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {services.map((service) => (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                  className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-300"
                >
                  <img 
                    src={service.image} 
                    alt={service.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                  
                  <div className="absolute top-4 sm:top-6 left-4 sm:left-6 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                    <i className={`${service.icon} text-3xl sm:text-4xl text-[#FFD700]`}></i>
                  </div>
                  
                  <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                    <h3 className="text-lg sm:text-2xl font-bold text-white mb-2">{service.name}</h3>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity text-[#FFD700] text-sm font-bold flex items-center gap-2">
                      Learn More
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>

                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#FFD700] rounded-xl transition-all"></div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas Section */}
        <section className="py-12 sm:py-20 bg-[#f5f5f5]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-5xl font-black text-[#1a1a1a] text-center mb-8 sm:mb-12">
              SERVING FRESNO &<br />SURROUNDING AREAS
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div className="relative h-64 sm:h-96 rounded-xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d206253.4068534778!2d-119.95326844999999!3d36.7468422!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80945de1549e4e9d%3A0x7b12406449a3b811!2sFresno%2C%20CA!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Fresno Service Area Map"
                ></iframe>
              </div>

              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#1a1a1a] mb-4 sm:mb-6">Service Coverage Areas</h3>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {serviceAreas.map((area, index) => (
                    <div key={index} className="flex items-center gap-2 sm:gap-3">
                      <i className="ri-map-pin-fill text-[#FFD700] text-lg sm:text-xl"></i>
                      <span className="text-gray-700 font-medium text-sm sm:text-base">{area}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-white rounded-lg shadow-md">
                  <p className="text-gray-600 text-sm sm:text-base mb-4">
                    We proudly serve Fresno and the surrounding Central Valley with professional electrical services. Our licensed electricians are ready to help with residential, commercial, and emergency electrical needs.
                  </p>
                  <a href="#get-quote" className="text-[#FFD700] font-bold hover:underline cursor-pointer text-sm sm:text-base">
                    Get a Free Quote →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Area Pages — Internal Linking Section */}
        <section className="py-12 sm:py-16 bg-white border-t border-gray-100">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-10">
              <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-4">
                Serving the Entire Central Valley
              </span>
              <h2 className="text-2xl sm:text-4xl font-black text-[#1a1a1a] mb-3">
                ELECTRICIAN SERVICES NEAR YOU
              </h2>
              <p className="text-gray-500 text-sm sm:text-base max-w-2xl mx-auto">
                Our licensed electricians travel throughout Fresno County, Kings County, and Tulare County. Find your local service area below.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { name: 'Clovis', slug: 'clovis', pop: '120k+' },
                { name: 'Madera', slug: 'madera', pop: '66k+' },
                { name: 'Visalia', slug: 'visalia', pop: '141k+' },
                { name: 'Hanford', slug: 'hanford', pop: '60k+' },
                { name: 'Tulare', slug: 'tulare', pop: '68k+' },
                { name: 'Selma', slug: 'selma', pop: '25k+' },
                { name: 'Sanger', slug: 'sanger', pop: '28k+' },
                { name: 'Reedley', slug: 'reedley', pop: '27k+' },
                { name: 'Dinuba', slug: 'dinuba', pop: '27k+' },
                { name: 'Porterville', slug: 'porterville', pop: '60k+' },
                { name: 'Fowler', slug: 'fowler', pop: '7k+' },
              ].map((area) => (
                <Link
                  key={area.slug}
                  to={`/areas/${area.slug}`}
                  className="bg-[#f5f5f5] rounded-xl p-4 text-center hover:border-[#FFD700] border-2 border-transparent transition-all group cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-map-pin-2-fill text-[#FFD700] text-2xl group-hover:scale-110 transition-transform"></i>
                  </span>
                  <p className="font-black text-[#1a1a1a] text-sm">{area.name}</p>
                  <p className="text-gray-500 text-xs mt-1">Pop. {area.pop}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Teaser Section */}
        <section className="py-12 sm:py-16 bg-[#f5f5f5]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 gap-4">
              <div>
                <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-3">
                  Electrical Guides &amp; Tips
                </span>
                <h2 className="text-2xl sm:text-4xl font-black text-[#1a1a1a]">
                  FRESNO ELECTRICIAN BLOG
                </h2>
              </div>
              <Link to="/blog" className="text-[#FFD700] font-bold hover:underline flex items-center gap-2 cursor-pointer whitespace-nowrap text-sm">
                View All Articles <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                {
                  slug: 'when-to-upgrade-electrical-panel-fresno',
                  title: 'How to Know When to Upgrade Your Electrical Panel in Fresno, CA',
                  cat: 'Residential',
                  img: 'https://readdy.ai/api/search-image?query=Close%20up%20view%20of%20a%20modern%20residential%20electrical%20breaker%20panel%20being%20inspected%20by%20a%20professional%20electrician%20in%20a%20Fresno%20California%20home%20showing%20neat%20organized%20wiring%20and%20circuit%20breakers%20with%20bright%20workshop%20lighting%20and%20professional%20safety%20equipment&width=600&height=400&seq=blog001thumb&orientation=landscape',
                  read: '6 min read',
                },
                {
                  slug: 'ev-charger-installation-fresno-homeowners',
                  title: 'EV Charger Installation Guide for Fresno Homeowners',
                  cat: 'EV & Solar',
                  img: 'https://readdy.ai/api/search-image?query=Modern%20white%20electric%20vehicle%20plugged%20into%20a%20Level%202%20home%20EV%20charging%20station%20mounted%20on%20a%20clean%20garage%20wall%20in%20a%20Fresno%20California%20suburban%20home%20with%20neat%20electrical%20wiring%20and%20professional%20installation%20showing%20charging%20overnight&width=600&height=400&seq=blog002thumb&orientation=landscape',
                  read: '7 min read',
                },
                {
                  slug: 'fresno-home-electrical-safety-checklist',
                  title: 'Fresno Home Electrical Safety Checklist for 2025',
                  cat: 'Safety',
                  img: 'https://readdy.ai/api/search-image?query=Professional%20licensed%20electrician%20using%20thermal%20imaging%20camera%20to%20inspect%20electrical%20wiring%20in%20a%20bright%20Fresno%20California%20residential%20home%20interior%20showing%20safety%20inspection%20process%20with%20modern%20testing%20equipment&width=600&height=400&seq=blog003thumb&orientation=landscape',
                  read: '8 min read',
                },
              ].map(post => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="group bg-white rounded-xl overflow-hidden border-2 border-transparent hover:border-[#FFD700] transition-all cursor-pointer">
                  <div className="h-48 overflow-hidden">
                    <img src={post.img} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-3">{post.cat}</span>
                    <h3 className="font-black text-[#1a1a1a] text-sm leading-snug mb-3">{post.title}</h3>
                    <span className="text-gray-400 text-xs">{post.read}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section className="py-12 sm:py-20 bg-[#1a1a1a] border-t-4 border-[#FFD700]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 sm:mb-12 gap-4">
              <h2 className="text-2xl sm:text-4xl font-black text-white">
                TRUSTED BY 500+ FRESNO HOMEOWNERS
              </h2>
              <div className="flex items-center gap-3">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-[#FFD700] text-xl sm:text-2xl"></i>
                  ))}
                </div>
                <span className="text-[#FFD700] text-2xl sm:text-3xl font-black">4.9/5.0</span>
              </div>
            </div>

            <div className="relative px-8 sm:px-12">
              <div className="overflow-hidden">
                <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${currentReview * 100}%)` }}>
                  {reviews.map((review) => (
                    <div key={review.id} className="w-full flex-shrink-0">
                      <div className="bg-white rounded-2xl p-6 sm:p-10 max-w-4xl mx-auto">
                        <div className="flex gap-1 mb-4">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <i key={star} className="ri-star-fill text-[#FFD700] text-lg sm:text-xl"></i>
                          ))}
                        </div>
                        <p className="text-gray-700 text-base sm:text-lg italic mb-4 sm:mb-6 leading-relaxed">
                          "{review.text}"
                        </p>
                        <div className="flex items-center gap-3 sm:gap-4">
                          <img 
                            src={review.avatar} 
                            alt={review.name}
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover"
                          />
                          <div>
                            <div className="font-bold text-[#1a1a1a] text-sm sm:text-base">{review.name}</div>
                            {review.verified && (
                              <div className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-bold px-3 py-1 rounded-full mt-1">
                                Verified Customer
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={prevReview}
                className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border-2 border-[#FFD700] text-[#FFD700] rounded-full hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line text-lg sm:text-xl"></i>
              </button>
              <button
                onClick={nextReview}
                className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border-2 border-[#FFD700] text-[#FFD700] rounded-full hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Get Quote Form Section */}
        <section id="get-quote" className="py-12 sm:py-20 bg-[#1a1a1a] border-t-4 border-[#FFD700]">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-3xl sm:text-5xl font-black text-white mb-4">GET A FREE QUOTE</h2>
              <p className="text-gray-300 text-base sm:text-lg">
                Fill out the form below and we'll get back to you within 24 hours
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-10 shadow-2xl" data-readdy-form id="homepage-quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-[#1a1a1a] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-[#1a1a1a] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors"
                    placeholder="(559) 555-1234"
                  />
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="email" className="block text-sm font-bold text-[#1a1a1a] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors"
                  placeholder="john@example.com"
                />
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="urgency" className="block text-sm font-bold text-[#1a1a1a] mb-2">
                  Urgency *
                </label>
                <select
                  id="urgency"
                  name="urgency"
                  value={formData.urgency}
                  onChange={handleFormChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors bg-white cursor-pointer"
                >
                  <option value="">Select urgency level...</option>
                  <option value="Emergency — Need help now">🚨 Emergency — Need help now</option>
                  <option value="Urgent — Within 24 hours">⚡ Urgent — Within 24 hours</option>
                  <option value="Soon — Within a few days">📅 Soon — Within a few days</option>
                  <option value="Planning — Just getting a quote">💬 Planning — Just getting a quote</option>
                </select>
              </div>

              <div className="mb-6 sm:mb-8">
                <label htmlFor="message" className="block text-sm font-bold text-[#1a1a1a] mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  maxLength={500}
                  rows={4}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about your electrical needs..."
                ></textarea>
              </div>

              {formStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border-2 border-green-500 rounded-lg flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                  <span className="text-sm font-medium text-green-700">
                    Thank you! We'll contact you within 24 hours.
                  </span>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg flex items-center gap-3">
                  <i className="ri-error-warning-fill text-red-500 text-xl"></i>
                  <span className="text-sm font-medium text-red-700">
                    Something went wrong. Please try again.
                  </span>
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'sending'}
                className="w-full bg-[#FFD700] text-[#1a1a1a] py-4 rounded-full font-bold text-sm uppercase tracking-widest hover:bg-[#FFC700] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'sending' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i>
                    Get Your Free Quote
                  </>
                )}
              </button>
            </form>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-4">
                FAQs
              </span>
              <h2 className="text-3xl sm:text-5xl font-black text-[#1a1a1a] mb-4">
                FRESNO ELECTRICIAN<br />FREQUENTLY ASKED QUESTIONS
              </h2>
              <p className="text-gray-500 text-base max-w-xl mx-auto">
                Everything you need to know about hiring a licensed electrician in Fresno, CA.
              </p>
            </div>
            <FaqAccordion />
          </div>
        </section>

        {/* Certifications Scroll */}
        <section className="py-8 sm:py-12 bg-[#f5f5f5] overflow-hidden">
          <div className="flex items-center gap-16 animate-scroll">
            {[...certifications, ...certifications].map((cert, index) => (
              <div key={index} className="flex items-center gap-4 whitespace-nowrap">
                <div className="text-center">
                  <div className="font-black text-[#1a1a1a] text-lg">{cert.name}</div>
                  <div className="text-gray-600 text-sm">{cert.description}</div>
                </div>
                {index < certifications.length * 2 - 1 && (
                  <div className="w-2 h-2 bg-[#FFD700] rounded-full"></div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Mobile Sticky Call Now Banner */}
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
          <a
            href="#get-quote"
            className="call-banner flex items-center justify-center gap-3 bg-[#FFD700] text-[#1a1a1a] py-4 px-6 w-full cursor-pointer"
          >
            <span className="w-6 h-6 flex items-center justify-center bolt-icon">
              <i className="ri-flashlight-fill text-xl"></i>
            </span>
            <span className="font-black text-base uppercase tracking-widest whitespace-nowrap">Get a Free Quote</span>
            <span className="w-6 h-6 flex items-center justify-center bolt-icon">
              <i className="ri-flashlight-fill text-lg"></i>
            </span>
          </a>
        </div>

        <Footer />
      </div>
    </>
  );
};

// --- FAQ Accordion Component ---
const faqs = [
  {
    q: 'Are you a licensed electrician in Fresno, CA?',
    a: 'Yes. Electrician Solutions Fresno holds a C-10 Electrical Contractor license issued by the California State License Board. We are NECA certified, fully insured, and bonded to work on all residential and commercial electrical projects throughout Fresno and the Central Valley.'
  },
  {
    q: 'Do you offer 24/7 emergency electrical services in Fresno?',
    a: 'Absolutely. We provide round-the-clock emergency electrical services across Fresno, Clovis, and surrounding areas. Whether it\'s a power outage, tripped breaker, or electrical hazard, our licensed Fresno electricians are available 24 hours a day, 7 days a week. Call (559) 555-1234 any time.'
  },
  {
    q: 'What areas near Fresno do you serve?',
    a: 'We serve all of Fresno CA including Downtown Fresno, North Fresno, Woodward Park, Fig Garden, Tower District, and Sunnyside. We also cover Clovis, Madera, Selma, Fowler, and the broader Central Valley region.'
  },
  {
    q: 'How much does an electrician cost in Fresno, CA?',
    a: 'Electrical service costs in Fresno vary by job type and complexity. Minor repairs typically start around $150–$300, while panel upgrades or full rewires can range from $1,500 to $5,000+. We offer free, no-obligation quotes — fill out our form or call (559) 555-1234 for an accurate estimate.'
  },
  {
    q: 'Do you install EV chargers and solar-ready wiring in Fresno?',
    a: 'Yes. We are Fresno\'s specialists in EV charging station installation and solar-ready electrical upgrades. Whether you drive a Tesla, Ford F-150 Lightning, or any other EV, our licensed electricians will install a Level 2 home charger safely and to California code.'
  },
  {
    q: 'Can you upgrade my electrical panel in Fresno?',
    a: 'Yes. Panel upgrades are one of our most common services in Fresno. We upgrade from 100A to 200A or 400A service, replace outdated Federal Pacific or Zinsco panels, and ensure your home meets current California electrical code. All work is permitted and inspected.'
  },
  {
    q: 'Are your Fresno electricians background checked and insured?',
    a: 'Yes. Every electrician on our Fresno team is background checked, drug tested, fully licensed, and covered by liability insurance and workers\' compensation. You can trust our team in your home or business.'
  },
  {
    q: 'Do you handle commercial electrical work in Fresno?',
    a: 'Yes. We provide full commercial electrical services in Fresno including tenant improvements, lighting retrofits, panel upgrades, data cabling, and code compliance work for offices, retail spaces, warehouses, and restaurants throughout the Fresno metro area.'
  },
];

function FaqAccordion() {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-3">
      {faqs.map((faq, i) => (
        <div
          key={i}
          className="border-2 border-gray-100 rounded-xl overflow-hidden transition-all"
          style={{ borderColor: open === i ? '#FFD700' : undefined }}
        >
          <button
            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left bg-white hover:bg-[#fffbea] transition-colors cursor-pointer"
            onClick={() => setOpen(open === i ? null : i)}
          >
            <span className="font-bold text-[#1a1a1a] text-sm sm:text-base">{faq.q}</span>
            <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-[#FFD700] text-[#1a1a1a]">
              <i className={`${open === i ? 'ri-subtract-line' : 'ri-add-line'} text-base`}></i>
            </span>
          </button>
          {open === i && (
            <div className="px-6 pb-5 bg-[#fffbea]">
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{faq.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default HomePage;